import { Arrow, ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { Button, IconButton, ResponsiveTableRow, Row, Separator, colors, H5 } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../../common/DmcResponsiveTable'
import { convertUTCString } from '../../../../util/DateUtil'
import { getIdElement } from '../../../../util/StringUtil'
import { ChatLink, CisLink } from '../../../app/Links'
import ChatMessage from './ChatMessage'
import { focusedMessageStyles } from '../styles'
import { sleep } from '../../../../util/Utils'
import { getChannelChatMessages } from '../../../../services/chatService'
import messageLogicOperators from '../enums'
import PreviousNext from './PreviousNext'

export default function ReplyTable({
  messageIdToFocus,
  redactedMessagesList,
  replyChannel,
  setChannelMessagesReplyThreads,
  expandedMessages,
  onMoreLessClick,
  redactSingleOnClick,
  appendSelectedMessages,
  removeSelectedMessages,
  isMessageInQuestion = false,
  resultsPerPage = 30,
}) {
  const { t } = useTranslation()
  const [selectedMessages, setSelectedMessages] = useState([])
  const [scrolledToMessage, setScrolledToMessage] = useState(false)
  const [fetchingImages, setFetchingImages] = useState()
  const repliesTableTopRef = useRef()
  const focusedReplyMessageIdRef = useRef()
  const hasImageAttachments = useMemo(
    () =>
      replyChannel.messages?.some((message) =>
        message.attachments?.some((attachment) => attachment.serviceType === 'artifact_store')
      ),
    [replyChannel.messages]
  )

  useEffect(() => {
    const scrollToMessage = async () => {
      if (!scrolledToMessage && replyChannel.messages && messageIdToFocus && focusedReplyMessageIdRef.current) {
        if (hasImageAttachments) {
          if (fetchingImages && fetchingImages.length === 0) {
            await sleep(1000)
            focusedReplyMessageIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            setScrolledToMessage(true)
          }
        } else {
          await sleep(1000)
          focusedReplyMessageIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setScrolledToMessage(true)
        }
      }
    }
    scrollToMessage()
  }, [
    replyChannel.messages,
    messageIdToFocus,
    scrolledToMessage,
    fetchingImages,
    hasImageAttachments,
    focusedReplyMessageIdRef,
  ])

  const setSelectedMessagesAndAllMessages = useCallback(
    (messages) => {
      if (messages.length === selectedMessages.length) {
        return
      }
      if (messages.length < selectedMessages.length) {
        const messagesToRemove = selectedMessages.filter((message) => !messages.includes(message))
        removeSelectedMessages(messagesToRemove)
      } else {
        const messagesToAppend = messages.filter((message) => !selectedMessages.includes(message))
        appendSelectedMessages(messagesToAppend)
      }
      setSelectedMessages(messages)
    },
    [appendSelectedMessages, removeSelectedMessages, selectedMessages]
  )

  const onPrevious = async () => {
    const response = await getChannelChatMessages(
      replyChannel.cid,
      resultsPerPage + 1,
      replyChannel.messages[0].messageId,
      messageLogicOperators.OLDER_THAN
    )
    let hasOlderReplies = false
    if (response.data.messages.length === resultsPerPage + 1) {
      hasOlderReplies = true
      response.data.messages.shift()
    }
    setChannelMessagesReplyThreads((prevState) => {
      return {
        ...prevState,
        [replyChannel.cid]: {
          ...response.data,
          hasNewerReplies: true,
          hasOlderReplies,
        },
      }
    })
  }

  const onNext = async () => {
    const response = await getChannelChatMessages(
      replyChannel.cid,
      resultsPerPage + 1,
      replyChannel.messages[replyChannel.messages.length - 1].messageId,
      messageLogicOperators.NEWER_THAN
    )
    let hasNewerReplies = false
    if (response.data.messages.length === resultsPerPage + 1) {
      hasNewerReplies = true
      response.data.messages.pop()
    }
    setChannelMessagesReplyThreads((prevState) => {
      return {
        ...prevState,
        [replyChannel.cid]: {
          ...response.data,
          hasNewerReplies,
          hasOlderReplies: true,
        },
      }
    })
  }

  const seeMoreButtonOnClick = () => {
    repliesTableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    onNext()
  }

  return (
    <>
      <Row gutters="nano" alignY="middle">
        <H5 supplementary>{`${t('chat.channelId', 'Channel ID')}:`}</H5>
        <span style={{ marginTop: '3px' }} growX ref={repliesTableTopRef}>
          <ChatLink channelId={replyChannel.cid} text={replyChannel.cid} />
        </span>
        <PreviousNext
          direction="vertical"
          onPrevious={onPrevious}
          onNext={onNext}
          disablePrevious={!replyChannel.hasOlderReplies}
          disableNext={!replyChannel.hasNewerReplies}
        />
      </Row>
      <div style={{ backgroundColor: colors.gray03 }}>
        <DmcResponsiveCheckboxTable
          totalColumnCount={5}
          onSelectionChange={setSelectedMessagesAndAllMessages}
          atSizeColumns={{
            default: {
              labels: [
                t('message', 'Message'),
                t('id', 'ID'),
                t('user', 'User'),
                t('updated', 'Updated'),
                `${t('redacted', 'Redacted')}/${t('deleted', 'Deleted')}`,
              ],
              columnWidths: [4, 2, 2, 2, 2],
            },
          }}
        >
          {replyChannel.messages.map((message) => {
            const messageId = `${message.messageId}${isMessageInQuestion ? '-messageInQuestion' : ''}`
            const shouldFocusMessageId = message.messageId === messageIdToFocus
            let shouldShowFocusMessageCss = false
            if (shouldFocusMessageId) {
              if (hasImageAttachments) {
                shouldShowFocusMessageCss = fetchingImages && fetchingImages.length === 0
              } else {
                shouldShowFocusMessageCss = true
              }
            }
            return (
              <ResponsiveTableRow
                key={message.messageId}
                name={messageId}
                disabled={
                  message.deletedAtDate || redactedMessagesList.includes(message.messageId)
                    ? {
                        Icon: ContentDelete,
                      }
                    : undefined
                }
                column1={
                  <ChatMessage
                    setFetchingImages={setFetchingImages}
                    message={message}
                    showMore={expandedMessages[messageId]}
                    onMoreLessClick={() => onMoreLessClick(messageId)}
                  />
                }
                column2={
                  // <span style={{ wordWrap: 'break-word' }}>
                  //   <ChatLink channelId={message.cid} text={message.messageId} messageId={message.messageId} />
                  // </span>
                  <div
                    css={shouldShowFocusMessageCss && focusedMessageStyles}
                    ref={shouldFocusMessageId ? focusedReplyMessageIdRef : undefined}
                  >
                    {message.messageId}
                  </div>
                }
                column3={
                  <span>
                    <CisLink userId={getIdElement(message.createdByCisId)} userName={message.createdByName} />
                  </span>
                }
                column4={message.updatedAtDate && convertUTCString(message.updatedAtDate, true)}
                column5={
                  message.deletedAtDate ? (
                    convertUTCString(message.deletedAtDate, true)
                  ) : (
                    <IconButton
                      Icon={ContentDelete}
                      onClick={() => redactSingleOnClick(message.messageId)}
                      color="red"
                    />
                  )
                }
              />
            )
          })}
        </DmcResponsiveCheckboxTable>
        <Separator />
        <Row alignX="center">
          {!replyChannel.hasNewerReplies ? (
            <H5 supplementary>{t('chat.messages.noMoreMessages', 'No more messages')}</H5>
          ) : (
            <Button emphasis="high" Icon={Arrow} iconDirection="down" onClick={seeMoreButtonOnClick}>
              {t('chat.messages.seeMore', 'See more')}
            </Button>
          )}
        </Row>
      </div>
    </>
  )
}
